import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Space, Select, DatePicker, Input, message, InputNumber, Modal, Divider } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { LeftOutlined, UserAddOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import StandardTable from '../../../components/common/StandardTable';
import Button from '../../../components/common/Button';
import { useGetMatGapWhInventory, useGetMatWarehouses } from '../../../hooks/genericHooks';
import type { VwMatGapWhInventory, MatWarehouses } from '../../../types';
import { insertHeaderWithItems, getTableNextSequenceValue } from '../../../services/api';
import { PersonnelSelect, WPermitSelect, ActivitySelections } from '../../../components/modals';

const { Title, Text } = Typography;

type InventoryRow = VwMatGapWhInventory & { key: string };

export default function MatGapIssueRequestCreate() {
  const navigate = useNavigate();
  const { data: invData = [], isLoading, refetch: refetchInventory } = useGetMatGapWhInventory() as any;
  const { data: whData = [] } = useGetMatWarehouses() as any;

  const inventory: VwMatGapWhInventory[] = Array.isArray(invData) ? invData : (invData?.data ?? []);
  const warehouses: MatWarehouses[] = Array.isArray(whData) ? whData : (whData?.data ?? []);

  const whOptions = useMemo(() => (warehouses || []).map(w => ({ value: w.id, label: w.name })), [warehouses]);
  const whMap = useMemo(() => {
    const m = new Map<number, string>();
    (warehouses || []).forEach(w => m.set(w.id, w.name));
    return m;
  }, [warehouses]);

  // Controls
  const [fromWarehouseId, setFromWarehouseId] = useState<number | undefined>(undefined);
  const [date, setDate] = useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const [search, setSearch] = useState<string>('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [extGroupFilter, setExtGroupFilter] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const [wpOpen, setWpOpen] = useState(false);
  const [wpRefNum, setWpRefNum] = useState<string>('');
  const [wpDesc, setWpDesc] = useState<string>('');
  const [wpActCode, setWpActCode] = useState<string>('');
  const [wpActName, setWpActName] = useState<string>('');

  const [actOpen, setActOpen] = useState(false);
  const [actCombined, setActCombined] = useState<string>('');
  const [actCode, setActCode] = useState<string>('');
  const [actName, setActName] = useState<string>('');

  // Derived final activity (for display and payload)
  const finalActCodeDisplay = wpRefNum ? (wpActCode || '') : (actCode || '');
  const finalActNameDisplay = wpRefNum ? (wpActName || '') : (actName || '');

  // Requester (use personnel modal once; parse code + label)
  const [requesterDisplay, setRequesterDisplay] = useState<string>(''); // Person full name
  const [requesterCode, setRequesterCode] = useState<string | null>(null);   // personnel code as string
  const [personnelOpen, setPersonnelOpen] = useState(false);

  // Required fields validation (placed after state declarations to avoid TS errors)
  const isRequesterValid = (requesterDisplay || '').trim().length > 0 && !!(requesterCode && String(requesterCode).trim().length > 0);
  const isDateValid = !!date;
  const isActivityValid = (finalActCodeDisplay || '').trim().length > 0 && (finalActNameDisplay || '').trim().length > 0;
  const disableSubmit = !isRequesterValid || !isDateValid || !isActivityValid;

  // Basket: accumulate selections across different filters
  // key => { row snapshot, qty }
  const [basket, setBasket] = useState<Record<string, { row: InventoryRow; qty: number }>>({});
  const [submitting, setSubmitting] = useState(false);

  const filtered: InventoryRow[] = useMemo(() => {
    const searchVal = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    const norm = (x: any) => (x == null ? '' : String(x)).trim().toLowerCase();

    const matchesSearch = (r: VwMatGapWhInventory) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      const hay = `${r.mat_id} ${r.description || ''} ${r.unit || ''} ${r.prod_group || ''} ${r.hierarchy_long || ''} ${r.ext_group || ''}`.toLowerCase();
      if (phraseMatches.length > 0) return phraseMatches.every(p => hay.includes(p));
      return tokens.every(t => hay.includes(t));
    };

    return (inventory || [])
      .filter(r => (fromWarehouseId == null || r.warehouse_id === fromWarehouseId))
      .filter(r => (!groupFilter || norm(r.prod_group) === norm(groupFilter)))
      .filter(r => (!extGroupFilter || norm(r.ext_group) === norm(extGroupFilter)))
      .filter(r => Number(r.onhand_qty || 0) > 0)
      .filter(matchesSearch)
      .map(r => ({ ...r, key: `${r.mat_id}-${r.warehouse_id}` }));
  }, [inventory, fromWarehouseId, search, groupFilter, extGroupFilter]);

  // Local selection state for current filtered view (derived from basket)
  const selectedKeys: React.Key[] = useMemo(() => {
    const keys = new Set(Object.keys(basket));
    return (filtered || []).filter(r => keys.has(r.key)).map(r => r.key);
  }, [basket, filtered]);

  const rowsByKey = useMemo(() => {
    const m: Record<string, InventoryRow> = {};
    filtered.forEach(r => { m[r.key] = r; });
    return m;
  }, [filtered]);

  const groupOptions = useMemo(() => {
    const norm = (s: any) => (s == null ? '' : String(s)).trim();
    const byKey = new Map<string, string>();
    const searchVal = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    const matchesSearch = (r: VwMatGapWhInventory) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      const hay = `${r.mat_id} ${r.description || ''} ${r.unit || ''} ${r.prod_group || ''} ${r.hierarchy_long || ''} ${r.ext_group || ''}`.toLowerCase();
      if (phraseMatches.length > 0) return phraseMatches.every(p => hay.includes(p));
      return tokens.every(t => hay.includes(t));
    };
    (inventory || [])
      .filter(r => fromWarehouseId == null || r.warehouse_id === fromWarehouseId)
      .filter(r => !extGroupFilter || norm(r.ext_group).toLowerCase() === norm(extGroupFilter).toLowerCase())
      .filter(matchesSearch)
      .forEach(r => {
        const raw = norm(r.prod_group);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [inventory, fromWarehouseId, extGroupFilter]);

  const extGroupOptions = useMemo(() => {
    const norm = (s: any) => (s == null ? '' : String(s)).trim();
    const byKey = new Map<string, string>();
    const searchVal = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    const matchesSearch = (r: VwMatGapWhInventory) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      const hay = `${r.mat_id} ${r.description || ''} ${r.unit || ''} ${r.prod_group || ''} ${r.hierarchy_long || ''} ${r.ext_group || ''}`.toLowerCase();
      if (phraseMatches.length > 0) return phraseMatches.every(p => hay.includes(p));
      return tokens.every(t => hay.includes(t));
    };
    (inventory || [])
      .filter(r => fromWarehouseId == null || r.warehouse_id === fromWarehouseId)
      .filter(r => !groupFilter || norm(r.prod_group).toLowerCase() === norm(groupFilter).toLowerCase())
      .filter(matchesSearch)
      .forEach(r => {
        const raw = norm(r.ext_group);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [inventory, fromWarehouseId, groupFilter]);

  // Auto-clear invalid selections when search or other filters remove available options
  React.useEffect(() => {
    if (groupFilter && !groupOptions.some(o => (o.value as string) === groupFilter)) {
      setGroupFilter('');
    }
  }, [groupOptions, groupFilter]);
  React.useEffect(() => {
    if (extGroupFilter && !extGroupOptions.some(o => (o.value as string) === extGroupFilter)) {
      setExtGroupFilter('');
    }
  }, [extGroupOptions, extGroupFilter]);

  const columns: ColumnsType<InventoryRow> = useMemo(() => [
    { title: 'Material ID', dataIndex: 'mat_id', key: 'mat_id', width: 130, sorter: (a, b) => String(a.mat_id || '').localeCompare(String(b.mat_id || '')) },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 90 },
    { title: 'Group', dataIndex: 'prod_group', key: 'prod_group', width: 160 },
    { title: 'Ext Group', dataIndex: 'ext_group', key: 'ext_group', width: 140 },
    { title: 'From WH', dataIndex: 'warehouse_id', key: 'warehouse_id', width: 160, render: (v: number) => whMap.get(v) || v },
    { title: 'Outstanding', dataIndex: 'requests', key: 'requests', width: 120, align: 'right', render: (v: any) => {
      const raw = Number(v ?? 0);
      if (!Number.isFinite(raw)) return 0;
      return Math.floor(raw * 1000) / 1000;
    } },
    { title: 'On-hand', dataIndex: 'onhand_qty', key: 'onhand_qty', width: 110, align: 'right', render: (v: any) => {
      const raw = Number(v ?? 0);
      if (!Number.isFinite(raw)) return 0;
      return Math.floor(raw * 1000) / 1000;
    }, sorter: (a, b) => (a.onhand_qty ?? 0) - (b.onhand_qty ?? 0) },
  ], [basket, whMap]);

  const basketItems = useMemo(() => Object.values(basket), [basket]);
  const selectedCount = useMemo(() => basketItems.length, [basketItems]);

  const handleSubmit = async () => {
    try {
      // Accept either a picked requester from modal (with code) or a typed non-empty display value
      if (!requesterDisplay || requesterDisplay.trim() === '') { message.warning('Please select requester'); return; }
      if (!date) { message.warning('Please select Request Date'); return; }
      // Determine final activity fields regardless of source (WP overrides Activity)
      const finalActCode = wpRefNum ? (wpActCode || '') : (actCode || '');
      const finalActName = wpRefNum ? (wpActName || '') : (actName || '');

      // Require at least one association (either WP or Activity)
      if (!wpRefNum && !actCode) {
        message.warning('Please select a Work Permit or an Activity');
        return;
      }
      if (!finalActCode || !finalActName) {
        message.warning('Missing final Activity Code/Name. Please complete the selection.');
        return;
      }
      if (basketItems.length === 0) { message.warning('Please select at least one material'); return; }

      // Build candidates from basket (independent of current filters)
      const candidates = basketItems.filter(x => x.qty > 0);

      if (candidates.length === 0) { message.warning('Please enter valid quantities'); return; }

      setSubmitting(true);

      // 1) Pre-fetch next id from sequence to construct ref_no (REQ-<7 digits>)
      const nextId = await getTableNextSequenceValue('mat_gap_issue_requests', 'id');
      if (!nextId) { throw new Error('Failed to get next request id from sequence'); }
      const refNo = `REQ-${String(nextId).padStart(7, '0')}`;

      // Parse and sanitize requester fields to ensure separate code and name
      const parseFromDisplay = (s: string) => {
        const str = String(s || '').trim();
        const m = str.match(/^([A-Za-z0-9_\-]+)\s*[-:]\s*(.+)$/);
        if (m) return { code: m[1].trim(), name: m[2].trim() };
        return { code: '', name: str };
      };
      const parsed = parseFromDisplay(requesterDisplay);
      const codeFromState = String(requesterCode || '').trim();
      const codeOnlyFromState = (() => {
        const m = codeFromState.match(/^([A-Za-z0-9_\-]+)/);
        return m ? m[1] : codeFromState;
      })();
      const finalRequesterCode = codeOnlyFromState || parsed.code;
      const sanitizedRequesterName = (() => {
        const base = (requesterDisplay || '').trim();
        const nameFromParsed = parsed.name;
        const codeToStrip = finalRequesterCode;
        if (!codeToStrip) return nameFromParsed || base;
        const re = new RegExp(`^${codeToStrip}\\s*[-:]?\\s*`, 'i');
        const stripped = base.replace(re, '').trim();
        return stripped || nameFromParsed || base;
      })();

      if (!finalRequesterCode || !sanitizedRequesterName) {
        message.warning('Requester parsing failed. Please re-select requester.');
        setSubmitting(false);
        return;
      }

      // 1b) Prepare request header with ref_no
      const header = {
        ref_no: refNo,
        requester: finalRequesterCode,           // code as string
        requester_name: sanitizedRequesterName,   // full name
        req_date: date,                     // request date (YYYY-MM-DD)
        notes: notes || null,
        // Only persist fields defined in MatGapIssueRequest type
        workpermit_ref_num: wpRefNum || null,
        activity_code: finalActCode,
        activity_name: finalActName,
      } as any;

      // 2) Insert items (batch) using ref_no FK
      const itemsPayload = candidates.map(x => ({
        ref_no: refNo,
        mat_id: x.row.mat_id,
        unit: x.row.unit,
        requested_qty: x.qty,
        warehouse_id: x.row.warehouse_id,
        notes: x.row.description || null,
      }));

      // 3) Atomic insert of header + items (transactional on backend)
      const trxResp = await insertHeaderWithItems({
        header_table: 'mat_gap_issue_requests',
        header_data: header,
        items_table: 'mat_gap_issue_request_items',
        items: itemsPayload,
      });
      const requestId = trxResp?.header_id || null;
      if (!requestId) { throw new Error('Failed to create request (header id missing)'); }

      message.success(`Request created. Ref: ${refNo}. Lines: ${itemsPayload.length}`);
      setBasket({});
      setNotes('');
      if (typeof refetchInventory === 'function') { await refetchInventory(); }
    } catch (err:any) {
      console.error(err);
      message.error(err?.message || 'Failed to create request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Create Issue Request</Title>
              <Text type="secondary">Select lines and quantities, then raise a request</Text>
            </div>
          </Space>
        </Col>
      </Row>

      {/* Filters row */}
      <Row justify="start" align="middle" gutter={[12, 8]} style={{ marginBottom: 16 }}>
        <Col>
          <Space wrap>
            <Select
              placeholder="From Warehouse (for filtering)"
              allowClear
              value={fromWarehouseId}
              onChange={(v) => {
                if (v == null) {
                  // Clearing filter: just set value, do not purge basket
                  setFromWarehouseId(v);
                  return;
                }
                // Changing to a specific warehouse: warn before purging items from other warehouses
                const targetWhName = whMap.get(v) || v;
                const toRemove = Object.values(basket).filter(x => x.row.warehouse_id !== v).length;
                if (toRemove > 0) {
                  Modal.confirm({
                    title: 'Change warehouse filter?',
                    content: `${toRemove} item(s) in basket are from other warehouse(s) and will be removed. Continue switching to '${targetWhName}'?`,
                    okText: 'Yes, switch',
                    cancelText: 'No',
                    onOk: () => {
                      setFromWarehouseId(v);
                      setBasket(prev => {
                        const next: typeof prev = {} as any;
                        Object.entries(prev).forEach(([k, val]) => {
                          if (val.row.warehouse_id === v) next[k] = val;
                        });
                        return next;
                      });
                      message.warning(`${toRemove} item(s) removed from basket due to warehouse filter change.`);
                    },
                  });
                } else {
                  setFromWarehouseId(v);
                }
              }}
              options={whOptions}
              style={{ width: 220 }}
            />
            <Select
              placeholder="Group"
              allowClear
              showSearch
              value={groupFilter || undefined}
              onChange={(v) => setGroupFilter(v || '')}
              options={groupOptions}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Ext Group"
              allowClear
              showSearch
              value={extGroupFilter || undefined}
              onChange={(v) => setExtGroupFilter(v || '')}
              options={extGroupOptions}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              style={{ width: 200 }}
            />
            <Input
              placeholder="Search material/description"
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 240 }}
            />
          </Space>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} style={{ paddingLeft: 8, display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button action="add" type="primary" onClick={handleSubmit} disabled={disableSubmit} loading={submitting}>{`Create Request (${selectedCount || 0})`}</Button>
            <Button onClick={() => { setFromWarehouseId(undefined); setDate(dayjs().format('YYYY-MM-DD')); setSearch(''); setGroupFilter(''); setExtGroupFilter(''); }}>Reset</Button>
          </Space>
        </Col>
      </Row>

      {/* Header inputs */}
      <Row gutter={[12, 8]} align="middle">
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input
            placeholder="Requester"
            value={requesterDisplay}
            onChange={(e) => setRequesterDisplay(e.target.value)}
            suffix={<Button size="small" icon={<UserAddOutlined />} onClick={() => setPersonnelOpen(true)} />}
            status={isRequesterValid ? undefined : 'error'}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Space>
            <Text strong>Request Date</Text>
            <DatePicker
              value={date ? dayjs(date) : null}
              onChange={(d) => setDate(d ? d.format('YYYY-MM-DD') : null)}
              style={{ width: 160 }}
              status={isDateValid ? undefined : 'error'}
            />
          </Space>
        </Col>
        <Col xs={24} md={24} lg={12}>
          <Input.TextArea
            placeholder="Request notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={1}
            allowClear
          />
        </Col>
        <Divider style={{ margin: '6px 0' }} />
      </Row>
      {/* Association selection */}
      <Row gutter={[12, 8]} align="middle">
        <Col span={24}>
          <Space align="center" wrap size="middle" style={{ width: '100%' }}>
            <Button style = {{width:200}} onClick={() => setWpOpen(true)}>Pick Work Permit</Button>
            {wpRefNum && (
              <Button onClick={() => { setWpRefNum(''); setWpDesc(''); setWpActCode(''); setWpActName(''); }}>Clear WP</Button>
            )}

            <Input
              placeholder="WP Ref - Desc"
              value={wpRefNum ? `${wpRefNum} - ${wpDesc}` : ''}
              readOnly
              style={{ width: 600 }}
            />
            <Button style = {{width:200}} onClick={() => setActOpen(true)} disabled={!!wpRefNum}>Pick Activity</Button>
            {actCode && !wpRefNum && (
              <Button onClick={() => { setActCombined(''); setActCode(''); setActName(''); }}>Clear Activity</Button>
            )}
          </Space>
        </Col>
      </Row>
      <Row gutter={[12, 8]} style={{ marginTop: 8 }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Input placeholder="Activity Code" value={finalActCodeDisplay} readOnly status={finalActCodeDisplay ? undefined : 'error'} />
        </Col>
        <Col xs={24} sm={12} md={16} lg={18}>
          <Input placeholder="Activity Name" value={finalActNameDisplay} readOnly status={finalActNameDisplay ? undefined : 'error'} />
        </Col>
      </Row>

      {/* Basket Summary */}
      <Row style={{ marginTop: 12 }}>
        <Col span={24}>
          <Card
            size="small"
            title={
              <span>
                <ShoppingCartOutlined style={{ marginRight: 6 }} />
                {`Basket (${selectedCount})`}
              </span>
            }
            bodyStyle={{ padding: 8, background: '#fffbe6' }}
          >
            {basketItems.length === 0 ? (
              <Text type="secondary">No items in basket. Select rows and set quantities to add.</Text>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Mat ID</th>
                      <th style={{ textAlign: 'left' }}>Description</th>
                      <th style={{ textAlign: 'left' }}>Unit</th>
                      <th style={{ textAlign: 'left' }}>Warehouse</th>
                      <th style={{ textAlign: 'right' }}>Qty</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {basketItems.map(({ row, qty }) => (
                      <tr key={row.key}>
                        <td>{row.mat_id}</td>
                        <td style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.description}</td>
                        <td>{row.unit}</td>
                        <td>{whMap.get(row.warehouse_id) || row.warehouse_id}</td>
                        <td style={{ textAlign: 'right' }}>
                          <InputNumber
                            min={0}
                            max={row.onhand_qty}
                            value={qty}
                            onChange={(v) => {
                              const q = typeof v === 'number' ? v : 0;
                              setBasket(prev => {
                                const next = { ...prev };
                                if (q > 0) next[row.key] = { row, qty: q }; else delete next[row.key];
                                return next;
                              });
                            }}
                            style={{ width: 120 }}
                          />
                        </td>
                        <td>
                          <Button size="small" danger onClick={() => setBasket(prev => { const n = { ...prev }; delete n[row.key]; return n; })}>Remove</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <StandardTable<InventoryRow>
        columns={columns}
        dataSource={filtered}
        rowKey={(r) => r.key}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: (keys, rows) => {
            const want = new Set((keys || []).map(k => String(k)));
            setBasket(prev => {
              const next = { ...prev } as Record<string, { row: InventoryRow; qty: number }>;
              // Adjust only for rows visible in current filtered view
              (filtered || []).forEach(r => {
                const k = String(r.key);
                if (want.has(k)) {
                  if (!next[k]) next[k] = { row: r, qty: Number(r.onhand_qty) || 0 };
                } else {
                  // Deselect from current view removes from basket
                  if (next[k]) delete next[k];
                }
              });
              return next;
            });
          },
          preserveSelectedRowKeys: true,
          getCheckboxProps: (record) => ({ disabled: (fromWarehouseId != null && record.warehouse_id !== fromWarehouseId) || (Number(record.onhand_qty || 0) <= 0) }),
        }}
        scroll={{ x: 'max-content' }}
      />

      <PersonnelSelect
        open={personnelOpen}
        multi={false}
        onCancel={() => setPersonnelOpen(false)}
        onOk={(selection: any) => {
          try {
            // Selection value may be either a code or an object; normalize to display string and code
            if (Array.isArray(selection)) {
              const code = String(selection[0]);
              const display = String(selection[1] ?? selection[0]);
              setRequesterCode(code);
              setRequesterDisplay(display);
            } else if (typeof selection === 'object') {
              const code = String(selection?.pers_id ?? selection?.code ?? '');
              const display = String(selection?.full_name ?? selection?.name ?? '');
              setRequesterCode(code || null);
              setRequesterDisplay(display);
            } else if (typeof selection === 'string') {
              setRequesterCode(selection);
              setRequesterDisplay(selection);
            }
          } catch {}
          setPersonnelOpen(false);
        }}
      />
      <WPermitSelect
        open={wpOpen}
        onCancel={() => setWpOpen(false)}
        onOk={([ref, desc, aCode, aName]) => {
          setWpRefNum(ref);
          setWpDesc(desc);
          setWpActCode(aCode);
          setWpActName(aName);
          // When a WP is selected, also reflect it into activity fields for display
          setActCombined(`${aCode} - ${aName}`);
          setActCode(aCode);
          setActName(aName);
          setWpOpen(false);
        }}
      />
      <ActivitySelections
        open={actOpen}
        onCancel={() => setActOpen(false)}
        onOk={(sel) => {
          if (Array.isArray(sel)) {
            const [combined, code, name] = sel as [string, string, string, number?];
            setActCombined(combined || `${code} - ${name}`);
            setActCode(code);
            setActName(name);
          } else if (typeof sel === 'string') {
            setActCombined(sel);
            setActCode(sel);
            setActName('');
          }
          // When activity is chosen directly, reset any WP selection
          setWpRefNum('');
          setWpDesc('');
          setWpActCode('');
          setWpActName('');
          setActOpen(false);
        }}
      />
    </Card>
  );
}
